"use client";

// CHAT-SWITCH-A: the full emoji picker (Slack's): search, frequently used (recents, kept in this
// browser), skin tone, every category. It is emoji-mart (MIT) with its own data set
// (@emoji-mart/data, MIT), both loaded on first open, so the app shell never pays for them and no
// emoji is fetched from a third party at run time. Native emoji glyphs, the app's light or dark.
import { SmilePlusIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../../cn";
import { useTheme } from "../../theme";
import { Button } from "../button";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";

type PickerModule = typeof import("emoji-mart");
type Selected = { native?: string };

let loaded: Promise<{ Picker: PickerModule["Picker"]; data: unknown }> | null = null;
/** emoji-mart and its data, loaded once (a retry after a failed load tries again). */
function loadPicker() {
  loaded ??= Promise.all([import("emoji-mart"), import("@emoji-mart/data")]).then(
    ([m, d]) => ({ Picker: m.Picker, data: (d as { default?: unknown }).default ?? d }),
    (e: unknown) => {
      loaded = null;
      throw e;
    },
  );
  return loaded;
}

export type EmojiPickerProps = {
  /** The chosen emoji (a native string, skin tone applied). */
  onPick: (emoji: string) => void;
  className?: string;
};

/** The picker itself (a search field first, then the frequently used row and the categories). */
export function EmojiPicker({ onPick, className }: EmojiPickerProps) {
  const host = React.useRef<HTMLDivElement | null>(null);
  const pick = React.useRef(onPick);
  pick.current = onPick;
  const { resolved } = useTheme();
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    let picker: HTMLElement | null = null;
    loadPicker().then(
      ({ Picker, data }) => {
        if (cancelled || !host.current) return;
        picker = new Picker({
          data,
          theme: resolved,
          set: "native",
          autoFocus: true,
          previewPosition: "none",
          skinTonePosition: "search",
          navPosition: "top",
          maxFrequentRows: 2,
          perLine: 8,
          onEmojiSelect: (e: Selected) => {
            if (e.native) pick.current(e.native);
          },
        }) as unknown as HTMLElement;
        host.current.replaceChildren(picker);
      },
      () => {
        if (!cancelled) setFailed(true);
      },
    );
    return () => {
      cancelled = true;
      picker?.remove();
    };
  }, [resolved]);
  if (failed)
    return (
      <p className="p-3 text-sm text-muted-foreground" role="alert">
        Emoji could not load. Check your connection and try again.
      </p>
    );
  return (
    <div
      ref={host}
      data-slot="emoji-picker"
      className={cn("min-h-[380px] min-w-[352px] [&_em-emoji-picker]:h-[380px]", className)}
    />
  );
}

export type EmojiPickerPopoverProps = {
  onPick: (emoji: string) => void;
  /** The button's accessible name and tooltip. */
  label?: string;
  /** A custom trigger (a Button); default a ghost smiley icon button. */
  trigger?: React.ReactElement;
  size?: "icon-xs" | "icon-sm";
  disabled?: boolean;
  align?: "start" | "center" | "end";
  side?: "top" | "bottom";
};

/** A smiley button that opens the picker in a popover; picking closes it. */
export function EmojiPickerPopover({
  onPick,
  label = "Add emoji",
  trigger,
  size = "icon-xs",
  disabled = false,
  align = "end",
  side = "bottom",
}: EmojiPickerPopoverProps) {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          trigger ?? (
            <Button
              variant="ghost"
              size={size}
              aria-label={label}
              title={label}
              disabled={disabled}
            />
          )
        }
      >
        {trigger ? undefined : <SmilePlusIcon />}
      </PopoverTrigger>
      <PopoverContent align={align} side={side} className="w-auto p-0" aria-label="Emoji">
        {open ? (
          <EmojiPicker
            onPick={(e) => {
              setOpen(false);
              onPick(e);
            }}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
